import { Splide, SplideSlide } from '@splidejs/react-splide';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'validate.js';

import { pushParams, resetParams } from 'dpl/actions/query_params';
import PreferredBreederBadge from 'dpl/breeder_preferred_qualifications/components/PreferredBreederBadge';
import Icon from 'dpl/common/components/Icon';
import {
  BREEDER_PROFILE_ID,
  TESTIMONIAL_MODAL_PAGE
} from 'dpl/constants/query_params';
import BreederProfileTestimonialModal, {
  useTestimonialModal
} from 'dpl/containers/BreederProfile/Index/BreederProfileTestimonialModal';
import UserAvatar from 'dpl/path_to_payment/components/UserAvatar';
import { nearbyOwnerStoriesResourceDefinition } from 'dpl/puppy_search_results/utils/queryDefinitions';
import useResourceQuery from 'dpl/shared/fetching/hooks/useResourceQuery';
import { isNumeric } from 'dpl/shared/form/utils';
import { truncateText } from 'dpl/shared/utils';
import { sendRudderstackEvent } from 'dpl/util/analytics';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_VIEWS,
  ANALYTICS_VIEW_FEATURES,
  ANALYTICS_VIEW_FEATURE_LOCATIONS
} from 'dpl/constants/analytics';

function ModulePagination({
  className,
  onNextClick,
  onPrevClick,
  pageCount,
  currentPage
}) {
  if (pageCount <= 1) return null;

  return (
    <div
      className={classnames(
        'flex flex-column items-end gap-8 mw-70-md mw-60-lg mw-50-xl',
        className
      )}
    >
      <div className="flex gap-24">
        <button
          className="pv2 ph2 br-100"
          type="button"
          aria-label="Previous story"
          onClick={onPrevClick}
        >
          <Icon name="fetch-arrow-left" height="24px" width="24px" />
        </button>
        <button
          className="pv2 ph2 br-100"
          type="button"
          aria-label="Next story"
          onClick={onNextClick}
        >
          <Icon name="fetch-arrow-right" height="24px" width="24px" />
        </button>
      </div>
      <div className="relative w-100" style={{ height: 2 }}>
        <span className="absolute bg-white db o-30 h-100 w-100" />
        <span
          className="absolute bg-white db h-100 transition"
          style={{
            width: `${(100 / pageCount) * currentPage}%`
          }}
        />
      </div>
    </div>
  );
}

ModulePagination.propTypes = {
  className: PropTypes.string,
  onNextClick: PropTypes.func.isRequired,
  onPrevClick: PropTypes.func.isRequired,
  pageCount: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired
};

ModulePagination.defaultProps = {
  className: null
};

export default function NearbyOwnerStoriesModule({
  breedId,
  className,
  citySlug
}) {
  const splideRef = useRef(null);
  const params = useSelector(server => server.queryParams);
  const dispatch = useDispatch();
  const { open: openModal } = useTestimonialModal();
  const [currentSlideNumber, setCurrentSlideNumber] = useState(1);
  const [selectedStory, setSelectedStory] = useState(null);

  const { Autoplay } = splideRef.current?.splide?.Components ?? {};

  function pauseAutoplay() {
    Autoplay?.pause();
  }

  function resumeAutoplay() {
    Autoplay?.play();
  }

  function sendAnalyticsEvent({ breederProfileId, buttonCta }) {
    sendRudderstackEvent(ANALYTICS_EVENTS.BUTTON_CLICKED, {
      button_cta: buttonCta,
      view: ANALYTICS_VIEWS.BREEDER_SEARCH_RESULTS,
      view_feature: ANALYTICS_VIEW_FEATURES.VO_STORIES_MODULE,
      view_feature_location: ANALYTICS_VIEW_FEATURE_LOCATIONS.SEARCH_RESULTS,
      breeder_profile_id: breederProfileId
    });
  }

  const {
    isLoading,
    value: { data: stories }
  } = useResourceQuery(nearbyOwnerStoriesResourceDefinition(breedId, citySlug));

  useEffect(() => {
    if (!isNumeric(params[TESTIMONIAL_MODAL_PAGE]) && !isEmpty(selectedStory)) {
      setSelectedStory(null);
    }
  }, [params]);

  useEffect(() => {
    if (selectedStory) {
      pauseAutoplay();

      dispatch(
        pushParams(
          { [BREEDER_PROFILE_ID]: selectedStory.breeder.id },
          { hash: true }
        )
      );
    } else {
      resumeAutoplay();
      dispatch(resetParams(BREEDER_PROFILE_ID, { hash: true }));
    }
  }, [selectedStory]);

  if (isLoading || isEmpty(stories)) return null;

  return (
    <div className={classnames('NearbyOwnerStoriesModule', className)}>
      <div className="row">
        <div className="flex flex-column col-12 col-md-6 justify-between">
          <h4 className="font-20 md:font-24 lg:font-32">
            Hear from Good Dog owners
          </h4>
          <div className="hidden md:block">
            <ModulePagination
              className="pb6"
              pageCount={stories.length}
              currentPage={currentSlideNumber}
              onPrevClick={() => splideRef.current.go('<')}
              onNextClick={() => splideRef.current.go('>')}
            />
          </div>
        </div>
        <div className="col-12 col-md-6 mt6 mt0-md">
          <Splide
            ref={splideRef}
            onActive={({ index }) => setCurrentSlideNumber(index + 1)}
            options={{
              rewind: true,
              autoplay: true,
              perPage: 1,
              gap: 72,
              arrows: false
            }}
          >
            {stories.map(story => {
              const { id, description, breeder, buyer } = story;
              const truncatedText = truncateText(description, 160);
              const hasMoreText = description !== truncatedText;
              const hasMoreContent = hasMoreText || story.has_photographs;
              const moreButtonLabel = hasMoreText ? 'Read more' : 'Show more';

              function trackMoreButtonClick() {
                sendAnalyticsEvent({
                  breederProfileId: breeder.id,
                  buttonCta: moreButtonLabel
                });
              }

              function trackProfileLinkClick() {
                sendAnalyticsEvent({
                  breederProfileId: breeder.id,
                  buttonCta: breeder.program_name
                });
              }

              function handleReadMoreClick() {
                setSelectedStory(story);
                openModal(story.id);
                trackMoreButtonClick();
              }

              return (
                <SplideSlide key={id}>
                  <a
                    className="flex items-center gap-12 md:gap-20 ml12-md"
                    href={breeder.profile_url}
                    target="_blank"
                    onClick={trackProfileLinkClick}
                    rel="noreferrer"
                  >
                    <div className="relative dib">
                      <UserAvatar
                        user={{
                          id: breeder.user_id,
                          initials: breeder.initials,
                          profile_photo_url: breeder.profile_photo_url
                        }}
                      />
                      {breeder.preferred && (
                        <PreferredBreederBadge
                          className="absolute right-0 bottom-0 nr1"
                          width="24px"
                          height="24px"
                          hideTooltip
                        />
                      )}
                    </div>
                    <div>
                      <p className="font-16 md:font-20 md:font-serif fw-medium">
                        {breeder.program_name}
                      </p>
                      <p className="font-14 md:font-16 o-60">
                        {breeder.description}
                      </p>
                    </div>
                  </a>
                  <blockquote className="relative flex flex-column flex-md-row font-serif mt5 mt12-md">
                    <span
                      className="db font-40 md:font-56 lg:font-72 lh-1 pl3-md pl0-lg"
                      style={{ minWidth: 48, maxWidth: 48 }}
                    >
                      “
                    </span>
                    <p className="font-20 md:font-24 lg:font-32 font-serif">
                      {truncatedText}
                    </p>
                  </blockquote>
                  {hasMoreContent && (
                    <button
                      type="button"
                      className="font-20 md:font-24 lg:font-32 font-serif underline mt4 mt10-md ml12-md"
                      onClick={handleReadMoreClick}
                    >
                      {moreButtonLabel}
                    </button>
                  )}
                  <div className="mt6 mt10-md ml12-md">
                    <p className="font-16 md:font-20 md:font-serif fw-medium">
                      {buyer.display_name}
                    </p>
                    <p className="font-14 md:font-16 o-60">
                      {buyer.description}
                    </p>
                  </div>
                </SplideSlide>
              );
            })}
          </Splide>
          <div className="md:hidden">
            <ModulePagination
              className="pb6"
              pageCount={stories.length}
              currentPage={currentSlideNumber}
              onPrevClick={() => splideRef.current.go('<')}
              onNextClick={() => splideRef.current.go('>')}
            />
          </div>
        </div>
      </div>
      <div className="sr-only">
        <button type="button" onClick={pauseAutoplay}>
          Pause
        </button>
        <button type="button" onClick={resumeAutoplay}>
          Play
        </button>
      </div>
      {selectedStory && (
        <BreederProfileTestimonialModal testimonialId={selectedStory.id} />
      )}
    </div>
  );
}

NearbyOwnerStoriesModule.propTypes = {
  breedId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  className: PropTypes.string,
  citySlug: PropTypes.string.isRequired
};

NearbyOwnerStoriesModule.defaultProps = {
  className: null
};
